import { FormikProps } from 'formik';
import { CardBody, CardHeader, Col, Input, Label, Row } from 'reactstrap';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { RootState } from 'slices';
import { useDispatch } from 'react-redux';
import {
  getTISIDistricts,
  getTISIProvinces,
  getTISISubDistricts,
} from 'services/locationService';
import {
  mapLocationsToOptions,
  mapProvincesToOptions,
  mapSubDistrictToOptions,
} from 'utils/mappers/locationMapper';
import {
  fetchDistrictsFailure,
  fetchDistrictsStart,
  fetchDistrictsSuccess,
  fetchProvincesFailure,
  fetchProvincesStart,
  fetchProvincesSuccess,
  fetchSubDistrictsFailure,
  fetchSubDistrictsStart,
  fetchSubDistrictsSuccess,
  resetDistricts,
  resetSubDistricts,
  selectDistrictsStatus,
  selectProvincesStatus,
  selectSubDistrictsStatus,
} from 'slices/locations/reducer';
import { CustomReactSelectStyles } from 'styles/common/singleSelectStyle';
import {
  CustomerBillingAddress,
  CustomerFormType,
} from 'types/customer/customerTypes';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useWindowSize from 'Components/Hooks/WindowSizeHook';
import 'styles/common/section-modal.css';
import { BOOTSTRAP_BREAKPOINTS } from 'utils/bootstrapBreakpoints';
import { UserInfo } from 'types/info/user';
import { CUSTOMER_NEW_ADDRESS_ALLOWED_STORE_IDS } from 'pages/Customers/CustomerConstants';

type Option = { label: string; value: string };

export default function CustomerAddressSection({
  formik,
}: {
  formik: FormikProps<CustomerFormType>;
}) {
  const location = useLocation();
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const dev = query.get('dev');
  const devByPass = dev === 'pos2u_dev!';

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDarkMode =
    useSelector((state: RootState) => state.Layout.layoutModeType) === 'dark';
  const { showAllValidateErrors } = useSelector((s: RootState) => s.Customer);

  const infoUser = useSelector(
    (state: RootState) => state.Info.user
  ) as UserInfo;
  const allowNewAddress = CUSTOMER_NEW_ADDRESS_ALLOWED_STORE_IDS.includes(
    infoUser?.store_id_login
  );

  // white list store id for allow new address
  useEffect(() => {
    if (!allowNewAddress && formik.values.customerAddress.isNewAddress) {
      formik.setFieldValue('customerAddress.isNewAddress', false);
    }
  }, [allowNewAddress, formik]);

  const locationsState = useSelector((state: RootState) => state.Locations);
  const {
    ProvincesOptions,
    DistrictsOptions,
    SubDistrictsOptions,
    provincesError,
    districtsError,
    subDistrictsError,
  } = locationsState;
  const { width } = useWindowSize();
  const isXSScreen = width <= BOOTSTRAP_BREAKPOINTS.sm;

  const LabelClass = 'col-12 col-sm-auto col-form-label label-fixed';
  const ColClass = 'col-fixed';
  const RowClass = 'row-fixed';
  const CardBodyClass = 'card-body-fixed';
  const ColLeftClass = 'col-left-fixed';
  const ColRightClass = 'col-right-fixed';
  const RightSideColClass = 'col-right-side-fixed';
  console.log('SubDistrictsOptions');
  console.log(SubDistrictsOptions);

  const err = (path: keyof CustomerBillingAddress) =>
    ((formik.touched.customerAddress as any)?.[path] &&
      (formik.errors.customerAddress as any)?.[path] &&
      typeof (formik.errors.customerAddress as any)[path] === 'string') ||
    (formik.isValid === false &&
      (formik.errors.customerAddress as any)?.[path] &&
      showAllValidateErrors === true);

  const getOpt = (opts: Option[], value?: string) =>
    value ? (opts.find((o) => o.value === value) ?? null) : null;

  const provincesStatus = useSelector(selectProvincesStatus);
  const districtsStatus = useSelector(selectDistrictsStatus);
  const subDistrictsStatus = useSelector(selectSubDistrictsStatus);

  const fetchProvinces = useCallback(async () => {
    dispatch(fetchProvincesStart());
    try {
      const response = await getTISIProvinces();
      const { message, error_code, data } = response ?? {};
      if (message === 'OK' && error_code === 0) {
        const provincesOptions = data?.provinces
          ? await mapProvincesToOptions(data.provinces)
          : [];
        dispatch(fetchProvincesSuccess(provincesOptions));
      } else {
        dispatch(fetchProvincesFailure(t('Invalid response')));
      }
    } catch (err) {
      console.error('Failed to fetch provinces:', err);
      dispatch(fetchProvincesFailure(t('Failed to load')));
    }
  }, [dispatch, t]);

  const fetchDistricts = useCallback(
    async (provinceId: number) => {
      if (!provinceId) return;
      dispatch(fetchDistrictsStart());
      try {
        const response = await getTISIDistricts(provinceId);
        const { message, error_code, data } = response ?? {};
        if (message === 'OK' && error_code === 0) {
          const districtsOptions = data?.districts
            ? await mapLocationsToOptions(data.districts)
            : [];
          dispatch(fetchDistrictsSuccess(districtsOptions));
        } else {
          dispatch(fetchDistrictsFailure(t('Invalid response')));
        }
      } catch (err) {
        console.error('Failed to fetch districts:', err);
        dispatch(fetchDistrictsFailure(t('Failed to load')));
      }
    },
    [dispatch, t]
  );

  const fetchSubDistricts = useCallback(
    async (districtId: number) => {
      if (!districtId) return;
      dispatch(fetchSubDistrictsStart());
      try {
        const response = await getTISISubDistricts(districtId);
        const { message, error_code, data } = response ?? {};
        if (message === 'OK' && error_code === 0) {
          const subDistrictsOptions = data?.subdistricts
            ? await mapSubDistrictToOptions(data.subdistricts)
            : [];
          dispatch(fetchSubDistrictsSuccess(subDistrictsOptions));
        } else {
          dispatch(fetchSubDistrictsFailure(t('Invalid response')));
        }
      } catch (err) {
        console.error('Failed to fetch subdistricts:', err);
        dispatch(fetchSubDistrictsFailure(t('Failed to load')));
      }
    },
    [dispatch, t]
  );

  useEffect(() => {
    if (provincesStatus === 'idle') {
      fetchProvinces();
    }
  }, [provincesStatus, fetchProvinces]);

  useEffect(() => {
    const pid = formik.values.customerAddress.province_id;
    dispatch(resetDistricts());
    dispatch(resetSubDistricts());

    if (pid && pid !== '') {
      const provinceIdNum = Number(pid);
      if (!Number.isNaN(provinceIdNum)) {
        fetchDistricts(provinceIdNum);
      }
    }
  }, [formik.values.customerAddress.province_id, fetchDistricts, dispatch]);

  useEffect(() => {
    const did = formik.values.customerAddress.district_id;
    dispatch(resetSubDistricts());

    if (did && did !== '') {
      const districtIdNum = Number(did);
      if (!Number.isNaN(districtIdNum)) {
        fetchSubDistricts(districtIdNum);
      }
    }
  }, [formik.values.customerAddress.district_id, fetchSubDistricts, dispatch]);

  return (
    <>
      <CardHeader className="mb-4 mx-0 px-0">
        <div
          className="px-0 mx-0"
          style={{
            fontSize: '1rem',
            fontWeight: '600',
            display: 'inline-flex',
          }}
        >
          <div className={`text-success`}>
            <i className="ri-user-settings-line"></i>
          </div>
          <div className="px-1">{t('Billing Address Information')}</div>
        </div>
      </CardHeader>
      <CardBody
        className={`${CardBodyClass} pb-0`}
        // style={{
        //   paddingLeft: isMobile ? '' : '2rem',
        //   paddingRight: isMobile ? '' : '2rem',
        // }}
      >
        {!devByPass && allowNewAddress && (
          <div className="mb-3 row">
            <Col className={`${LabelClass}`}></Col>
            <Col>
              <div className="mt-1 form-check form-switch gap-2 form-switch-success d-flex flex-row-reverse align-items-center justify-content-end">
                <Label
                  className="form-check-label"
                  htmlFor={`customerAddress.isNewAddress`}
                >
                  {t('Use a new address')}
                </Label>
                <Input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id={`customerAddress.isNewAddress`}
                  name={`customerAddress.isNewAddress`}
                  defaultChecked={formik.values.customerAddress.isNewAddress}
                  disabled={formik.values.customerAddress.isNewAddress}
                  onChange={(e) => {
                    if (e.target.checked) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                />
              </div>
            </Col>
          </div>
        )}

        <div className="mb-3 row">
          <Label
            htmlFor="customerAddress.address_1"
            className={`${LabelClass}`}
          >
            <div className="d-flex">{t('Address')}</div>
          </Label>
          <Col className={`${ColClass}`}>
            <textarea
              id="customerAddress.address_1"
              name="customerAddress.address_1"
              className="form-control"
              rows={2}
              maxLength={255}
              value={formik.values.customerAddress.address_1}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {err('address_1') ? (
              <div className="text-danger">
                {(formik.errors.customerAddress as any).address_1}
              </div>
            ) : null}
            <div className="text-muted pt-1" style={{ fontSize: '0.75rem' }}>
              {formik.values.customerAddress.isNewAddress || devByPass
                ? t('NewAddressDescription')
                : t('OldAddressDescription')}
            </div>
          </Col>
        </div>

        {(formik.values.customerAddress.isNewAddress || devByPass) && (
          <div className={`${isXSScreen ? '' : 'mb-3'} row`}>
            <Col lg={6} className="px-0">
              <Row className={`${RowClass} px-0 mx-0`}>
                <Label
                  htmlFor="customerAddress.province_id"
                  className="col-sm-3 col-xs-auto col-form-label"
                >
                  {t('Province')}
                </Label>
                <Col sm={9} className={`col-sm-auto ${ColLeftClass}`}>
                  <Select
                    styles={CustomReactSelectStyles(isDarkMode)}
                    inputId="customerAddress.province_id"
                    name="customerAddress.province_id"
                    options={ProvincesOptions}
                    value={getOpt(
                      ProvincesOptions,
                      formik.values.customerAddress.province_id
                    )}
                    isLoading={provincesStatus === 'loading'}
                    loadingMessage={() => t('Loading...')}
                    noOptionsMessage={() =>
                      provincesError ? provincesError : t('No options')
                    }
                    onChange={(opt: any) => {
                      formik
                        .setFieldValue(
                          'customerAddress.province_id',
                          opt?.value ?? ''
                        )
                        .then(() => {
                          formik.setFieldTouched(
                            'customerAddress.province_id',
                            true
                          );
                          formik.setFieldValue(
                            'customerAddress.district_id',
                            ''
                          );
                          formik.setFieldValue(
                            'customerAddress.sub_district_id',
                            ''
                          );
                        });
                    }}
                    onBlur={() =>
                      formik.setFieldTouched(
                        'customerAddress.province_id',
                        true
                      )
                    }
                    placeholder={t('Select province')}
                  />
                  {err('province_id') ? (
                    <div className="text-danger">
                      {(formik.errors.customerAddress as any).province_id}
                    </div>
                  ) : null}
                </Col>
              </Row>
            </Col>
            <Col lg={6} className={`${RightSideColClass}`}>
              <Row className={`${RowClass} px-0 mx-0`}>
                <Label
                  htmlFor="customerAddress.district_id"
                  className="col-xs-auto col-sm-3 col-form-label"
                >
                  {t('District')}
                </Label>

                <Col sm={9} className={`col-sm-auto mx-0 ${ColRightClass}`}>
                  <Select
                    isDisabled={
                      formik.values.customerAddress.province_id === ''
                    }
                    styles={CustomReactSelectStyles(isDarkMode)}
                    inputId="customerAddress.district_id"
                    name="customerAddress.district_id"
                    options={DistrictsOptions}
                    value={getOpt(
                      DistrictsOptions,
                      formik.values.customerAddress.district_id
                    )}
                    isLoading={districtsStatus === 'loading'}
                    loadingMessage={() => t('Loading...')}
                    noOptionsMessage={() =>
                      districtsError ? districtsError : t('No options')
                    }
                    onChange={(opt: any) => {
                      formik
                        .setFieldValue(
                          'customerAddress.district_id',
                          opt?.value ?? ''
                        )
                        .then(() => {
                          formik.setFieldTouched(
                            'customerAddress.district_id',
                            true
                          );
                          formik.setFieldValue(
                            'customerAddress.sub_district_id',
                            ''
                          );
                        });
                    }}
                    onBlur={() =>
                      formik.setFieldTouched(
                        'customerAddress.district_id',
                        true
                      )
                    }
                    placeholder={t('Select district')}
                  />
                  {err('district_id') ? (
                    <div className="text-danger">
                      {(formik.errors.customerAddress as any).district_id}
                    </div>
                  ) : null}
                </Col>
              </Row>
            </Col>
          </div>
        )}

        {(formik.values.customerAddress.isNewAddress || devByPass) && (
          <>
            <div className={`${isXSScreen ? '' : 'mb-3'} row`}>
              <Col lg={6} className="px-0">
                <Row className={`${RowClass} px-0 mx-0`}>
                  <Label
                    htmlFor="customerAddress.sub_district_id"
                    className="col-sm-3 col-xs-auto col-form-label"
                  >
                    {t('Sub-district')}
                  </Label>
                  <Col sm={9} className={`col-sm-auto ${ColLeftClass}`}>
                    <Select
                      isDisabled={
                        formik.values.customerAddress.district_id === ''
                      }
                      styles={CustomReactSelectStyles(isDarkMode)}
                      inputId="customerAddress.sub_district_id"
                      name="customerAddress.sub_district_id"
                      options={SubDistrictsOptions}
                      value={getOpt(
                        SubDistrictsOptions,
                        formik.values.customerAddress.sub_district_id
                      )}
                      isLoading={subDistrictsStatus === 'loading'}
                      loadingMessage={() => t('Loading...')}
                      noOptionsMessage={() =>
                        subDistrictsError ? subDistrictsError : t('No options')
                      }
                      onChange={(opt: any) => {
                        formik
                          .setFieldValue(
                            'customerAddress.sub_district_id',
                            opt?.value ?? ''
                          )
                          .then(() =>
                            formik.setFieldTouched(
                              'customerAddress.sub_district_id',
                              true
                            )
                          );
                        formik.setFieldValue(
                          'customerAddress.zip',
                          opt?.zip ?? ''
                        );
                      }}
                      onBlur={() =>
                        formik.setFieldTouched(
                          'customerAddress.sub_district_id',
                          true
                        )
                      }
                      placeholder={t('Select sub-district')}
                    />
                    {err('sub_district_id') ? (
                      <div className="text-danger">
                        {(formik.errors.customerAddress as any).sub_district_id}
                      </div>
                    ) : null}
                  </Col>
                </Row>
              </Col>
              <Col lg={6} className={`${RightSideColClass}`}>
                <Row className={`${RowClass} px-0 mx-0`}>
                  <Label
                    htmlFor="customerAddress.zip"
                    className="col-xs-auto col-sm-3 col-form-label"
                  >
                    {t('Postal Code')}
                  </Label>

                  <Col sm={9} className={`col-sm-auto mx-0 ${ColRightClass}`}>
                    <Input
                      type="text"
                      id="customerAddress.zip"
                      name="customerAddress.zip"
                      className="form-control"
                      maxLength={10}
                      value={formik.values.customerAddress.zip}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      // disabled={formik.values.customerAddress.isNewAddress}
                    />
                    {err('zip') ? (
                      <div className="text-danger">
                        {(formik.errors.customerAddress as any).zip}
                      </div>
                    ) : null}
                  </Col>
                </Row>
              </Col>
            </div>
          </>
        )}

        {(!formik.values.customerAddress.isNewAddress || devByPass) && (
          <>
            <div className="mb-3 row">
              <Label htmlFor="customerAddress.city" className={`${LabelClass}`}>
                {t('District')}
              </Label>
              <Col>
                <Input
                  type="text"
                  className="form-control"
                  id="customerAddress.city"
                  name="customerAddress.city"
                  value={formik.values.customerAddress.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  maxLength={255}
                />
                {(formik.touched.customerAddress?.city &&
                  formik.errors.customerAddress?.city &&
                  typeof formik.errors.customerAddress?.city === 'string') ||
                (formik.isValid === false &&
                  formik.errors.customerInfo?.accountNumber &&
                  showAllValidateErrors === true) ? (
                  <div className={`text-danger `}>
                    {formik.errors.customerAddress?.city}
                  </div>
                ) : null}
              </Col>
            </div>
            <div className="mb-3 row">
              <Label
                htmlFor="customerAddress.state"
                className={`${LabelClass}`}
              >
                {t('Province')}
              </Label>
              <Col>
                <Input
                  type="text"
                  className="form-control"
                  id="customerAddress.state"
                  name="customerAddress.state"
                  value={formik.values.customerAddress.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  maxLength={255}
                />
                {(formik.touched.customerAddress?.state &&
                  formik.errors.customerAddress?.state &&
                  typeof formik.errors.customerAddress?.state === 'string') ||
                (formik.isValid === false &&
                  formik.errors.customerInfo?.accountNumber &&
                  showAllValidateErrors === true) ? (
                  <div className={`text-danger `}>
                    {formik.errors.customerAddress?.state}
                  </div>
                ) : null}
              </Col>
            </div>
            <div className="mb-3 row">
              <Label htmlFor="customerAddress.zip" className={`${LabelClass}`}>
                {t('Postal Code')}
              </Label>
              <Col>
                <Input
                  type="text"
                  id="customerAddress.zip"
                  name="customerAddress.zip"
                  className="form-control"
                  maxLength={10}
                  value={formik.values.customerAddress.zip}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  // disabled={formik.values.customerAddress.isNewAddress}
                />
                {err('zip') ? (
                  <div className="text-danger">
                    {(formik.errors.customerAddress as any).zip}
                  </div>
                ) : null}
              </Col>
            </div>
          </>
        )}

        <div className="mb-3 row">
          <Label
            htmlFor="customerAddress.shippingAddress"
            className={`${LabelClass}`}
          >
            {t('Shipping Address')}
          </Label>
          <Col className={`${ColClass}`}>
            <textarea
              id="customerAddress.shippingAddress"
              name="customerAddress.shippingAddress"
              className="form-control"
              rows={2}
              maxLength={255}
              value={formik.values.customerAddress.shippingAddress}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {err('shippingAddress') ? (
              <div className="text-danger">
                {(formik.errors.customerAddress as any).shippingAddress}
              </div>
            ) : null}
          </Col>
        </div>
        <div className="mb-3 row"></div>
      </CardBody>
    </>
  );
}
